import type {
  HotelInfoEnrichment,
  HotelOffer,
  HotelRate,
  HotelRoom,
  HotelSearchInfo,
} from '../api/hotels.types';
import { formatOfferLabels } from './formatProviderLabel';
import type {
  CombinationRoom,
  HotelDetailData,
  HotelRoomOption,
  RateFeature,
  SortedRate,
} from '../types/hotels.presentation';

interface DetailMappingOptions {
  searchToken?: string;
  info?: HotelSearchInfo;
  /** Destination name selected by the user in the search form, used when the provider sends none. */
  fallbackDestination?: string;
}

// Never exposes provider tokens/keys.
function buildRateFeatures(rate: HotelRate | undefined): RateFeature[] {
  if (!rate) return [];

  const features: RateFeature[] = [];

  // Room/rate name (display-only)
  if (rate.name) {
    features.push({ type: 'room', label: rate.name, value: rate.name });
  }

  // Meal plan (real stream `meal` preferred, legacy `board` as fallback)
  const mealPlan = rate.meal || rate.board;
  if (mealPlan) {
    features.push({ type: 'meal', label: mealPlan, value: mealPlan });
  }

  // Refundability — use refundable field ("R"/"NR"/"ND"); nonRefundable is deprecated
  if (rate.refundable === 'R' || rate.refundable === true) {
    features.push({
      type: 'refundability',
      label: 'hotels.detail.refundable',
      value: 'hotels.detail.refundable',
    });
  } else if (rate.refundable === 'NR') {
    features.push({
      type: 'refundability',
      label: 'hotels.detail.nonRefundable',
      value: 'hotels.detail.nonRefundable',
    });
  }

  return features;
}

// Falls back to i18n key when no human label exists. Never exposes provider tokens/keys.
function buildRateSummary(rate: HotelRate | undefined): string {
  if (!rate) return 'hotels.results.rateUnavailable';

  const parts: string[] = [];

  // Room/rate name
  if (rate.name) parts.push(rate.name);

  // Meal plan (real stream `meal` preferred, legacy `board` as fallback)
  const mealPlan = rate.meal || rate.board;
  if (mealPlan) parts.push(mealPlan);

  // Refundability — use refundable field ("R"/"NR"/"ND"); nonRefundable is deprecated
  if (rate.refundable === 'R' || rate.refundable === true) {
    parts.push('hotels.detail.refundable');
  } else if (rate.refundable === 'NR') {
    parts.push('hotels.detail.nonRefundable');
  }

  if (parts.length === 0) return 'hotels.results.rateUnavailable';
  return parts.join(' · ');
}

function normalizeRefundable(value: boolean | string | undefined): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (value === 'R') return true;
  if (value === 'NR' || value === 'ND') return false;
  return undefined;
}

function readRatePrice(rate: HotelRate): {
  net: number;
  sale: number;
  sortNet: number;
  sortSale: number;
} {
  const price = (rate as { price?: { net?: unknown; sale?: unknown } }).price;
  const net = typeof price?.net === 'number' && Number.isFinite(price.net) ? price.net : undefined;
  const sale =
    typeof price?.sale === 'number' && Number.isFinite(price.sale) ? price.sale : undefined;

  return {
    net: net ?? 0,
    sale: sale ?? 0,
    sortNet: net ?? Number.POSITIVE_INFINITY,
    sortSale: sale ?? Number.POSITIVE_INFINITY,
  };
}

function compareRatesByPrice(a: HotelRate, b: HotelRate): number {
  const aPrice = readRatePrice(a);
  const bPrice = readRatePrice(b);
  const saleDiff = aPrice.sortSale - bPrice.sortSale;
  if (!Number.isNaN(saleDiff) && saleDiff !== 0) return saleDiff;

  const netDiff = aPrice.sortNet - bPrice.sortNet;
  return Number.isNaN(netDiff) ? 0 : netDiff;
}

function isValidRatePrice(sale: number): boolean {
  return Number.isFinite(sale) && sale > 0;
}

const ROOM_OPTION_FALLBACK_KEY = 'hotels.detail.roomOptionFallback';

/**
 * Upper bound of combinations kept per offer. Multi-entry offers almost always carry one rate
 * per entry; the bound only matters if a provider sends several rates on several entries.
 */
const MAX_COMBINATIONS_PER_OFFER = 60;

function toCount(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : fallback;
}

function trimmed(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function uniqueJoin(values: (string | undefined)[], separator = ' / '): string | undefined {
  const unique = [...new Set(values.filter((value): value is string => !!value))];
  return unique.length > 0 ? unique.join(separator) : undefined;
}

function sameValue(values: (string | undefined)[]): string | undefined {
  const [first] = values;
  return first && values.every((value) => value === first) ? first : undefined;
}

function combineRefundable(values: (boolean | undefined)[]): boolean | undefined {
  if (values.some((value) => value === false)) return false;
  if (values.length > 0 && values.every((value) => value === true)) return true;
  return undefined;
}

function sumPrices(rates: HotelRate[]): { net: number; sale: number } {
  return rates.reduce(
    (total, rate) => {
      const price = readRatePrice(rate);
      return { net: total.net + price.net, sale: total.sale + price.sale };
    },
    { net: 0, sale: 0 },
  );
}

function buildCombinationRoom(room: HotelRoom, rate: HotelRate): CombinationRoom {
  const childrenAges = (room.occupancy?.ages ?? [])
    .map(Number)
    .filter((age) => Number.isFinite(age));

  return {
    name: trimmed(rate.name) ?? trimmed(room.type),
    units: Math.max(1, toCount(room.units, 1)),
    adults: toCount(room.occupancy?.adults, 0),
    children: toCount(room.occupancy?.children, childrenAges.length),
    childrenAges,
  };
}

/** "Doble", "2 × Doble" or "Doble + Triple"; undefined when any room lacks a name. */
function combinationName(rooms: CombinationRoom[]): string | undefined {
  if (rooms.some((room) => !room.name)) return undefined;

  const unitsByName = new Map<string, number>();
  for (const room of rooms) {
    unitsByName.set(room.name, (unitsByName.get(room.name) ?? 0) + room.units);
  }

  return [...unitsByName]
    .map(([name, units]) => (units > 1 ? `${units} × ${name}` : name))
    .join(' + ');
}

function buildCombination(offer: HotelOffer, picks: HotelRate[]): SortedRate {
  const rooms = picks.map((rate, index) => buildCombinationRoom(offer.rooms[index], rate));
  const rateKeys = picks.map((rate) => rate.key);
  const price = sumPrices(picks);
  const remarks = picks.map((rate) => normalizeRemarkText(rate.remarks));

  return {
    key: rateKeys.join('|'),
    rateKeys,
    offerToken: offer.token,
    supplier: offer.supplier,
    currency: offer.currency,
    rooms,
    name: combinationName(rooms),
    meal: uniqueJoin(picks.map((rate) => trimmed(rate.meal))),
    mealType: sameValue(picks.map((rate) => trimmed(rate.mealType))),
    board: uniqueJoin(picks.map((rate) => trimmed(rate.board))),
    refundable: combineRefundable(picks.map((rate) => normalizeRefundable(rate.refundable))),
    cancellation: uniqueJoin(
      picks.map((rate) => trimmed(rate.cancellation)),
      ' · ',
    ),
    remarks: picks.length === 1 ? picks[0].remarks : uniqueJoin(remarks, ' · '),
    promotionLabel: picks.map((rate) => extractPromotionLabel(rate.remarks)).find(Boolean),
    net: price.net,
    sale: price.sale,
  };
}

/**
 * Every bookable combination of an offer: one rate per entry of `offer.rooms[]`. With a
 * single room this is one combination per rate; in multi-room searches each offer is already
 * a provider-built combination, so it usually yields one (or one per alternative rate of an
 * entry grouping identical rooms with `units > 1`).
 */
export function buildOfferCombinations(offer: HotelOffer): SortedRate[] {
  const entries = offer.rooms ?? [];
  if (entries.length === 0 || !offer.token) return [];

  const ratesByEntry = entries.map((room) =>
    (room.rates ?? [])
      .filter((rate) => rate?.key && isValidRatePrice(readRatePrice(rate).sale))
      .sort(compareRatesByPrice),
  );
  if (ratesByEntry.some((rates) => rates.length === 0)) return [];

  let picks: HotelRate[][] = [[]];
  for (const rates of ratesByEntry) {
    picks = picks.flatMap((partial) => rates.map((rate) => [...partial, rate]));
    if (picks.length > MAX_COMBINATIONS_PER_OFFER) {
      picks = picks
        .sort((a, b) => sumPrices(a).sale - sumPrices(b).sale)
        .slice(0, MAX_COMBINATIONS_PER_OFFER);
    }
  }

  return picks.map((combination) => buildCombination(offer, combination));
}

function compareCombinations(a: SortedRate, b: SortedRate): number {
  return a.sale - b.sale || a.net - b.net;
}

function normalizeIdentityText(value: string | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Same rooms, board and refundability = same option for the user, whatever the provider. */
function combinationIdentity(rate: SortedRate): string {
  const rooms = rate.rooms?.length
    ? rate.rooms
        .map((room) => `${normalizeIdentityText(room.name)}×${room.units}`)
        .sort()
        .join('+')
    : normalizeIdentityText(rate.name);
  const board = rate.mealType || normalizeIdentityText(rate.meal || rate.board);
  return `${rooms}|${board}|${String(rate.refundable)}`;
}

/** Keeps the cheapest of the combinations repeated across providers/offers. */
function dedupeCombinations(rates: SortedRate[]): SortedRate[] {
  const cheapest = new Map<string, SortedRate>();
  for (const rate of [...rates].sort(compareCombinations)) {
    const identity = combinationIdentity(rate);
    if (!cheapest.has(identity)) cheapest.set(identity, rate);
  }
  return [...cheapest.values()];
}

/**
 * Groups combinations into room options (one card per room composition), cheapest first.
 * Inside each option the rates are the alternatives (board, refundability) for those rooms.
 */
function buildRoomOptionsFromCombinations(
  combinations: SortedRate[],
  fallback: { currency: string; supplier: string },
): HotelRoomOption[] {
  const byName = new Map<string, SortedRate[]>();
  for (const rate of dedupeCombinations(combinations)) {
    const type = rate.name ?? ROOM_OPTION_FALLBACK_KEY;
    if (!byName.has(type)) byName.set(type, []);
    byName.get(type).push(rate);
  }

  return [...byName]
    .map(([type, rates]): HotelRoomOption => {
      const best = rates[0];
      return {
        type,
        bestRateKey: best.key,
        board: best.meal || best.board,
        refundable: best.refundable,
        cancellation: best.cancellation,
        net: best.net,
        sale: best.sale,
        currency: best.currency ?? fallback.currency,
        supplier: best.supplier ?? fallback.supplier,
        rateCount: rates.length,
        rates,
      };
    })
    .sort((a, b) => a.sale - b.sale || a.net - b.net);
}

function selectBestCombination(roomOptions: HotelRoomOption[]): SortedRate | undefined {
  return roomOptions
    .map((option) => option.rates[0])
    .filter(Boolean)
    .sort(compareCombinations)[0];
}

/** Adapts a combination to the rate shape used by the card summary/feature builders. */
function combinationAsRate(rate: SortedRate | undefined): HotelRate | undefined {
  if (!rate) return undefined;
  return {
    key: rate.key,
    name: rate.name,
    meal: rate.meal,
    board: rate.board,
    refundable: rate.refundable === undefined ? undefined : rate.refundable ? 'R' : 'NR',
    price: { net: rate.net, sale: rate.sale },
  };
}

function buildRevalidation(
  searchToken: string | undefined,
  best: SortedRate | undefined,
): HotelDetailData['revalidation'] {
  if (!searchToken || !best?.offerToken) return undefined;
  return { searchToken, offerToken: best.offerToken, rateKeys: best.rateKeys ?? [best.key] };
}

/**
 * Merges the detail of the same hotel coming from another offer/provider: every combination
 * stays available in the detail (deduplicated by composition, cheapest wins) while the card
 * keeps showing the overall cheapest one as its "from" price.
 */
export function mergeHotelDetails(
  primary: HotelDetailData,
  other: HotelDetailData,
): HotelDetailData {
  const roomOptions = buildRoomOptionsFromCombinations(
    [...primary.roomOptions, ...other.roomOptions].flatMap((option) => option.rates),
    { currency: primary.currency, supplier: primary.supplier },
  );
  const best = selectBestCombination(roomOptions);
  const availableMealTypes = collectMealTypes(roomOptions);
  const searchToken = primary.revalidation?.searchToken ?? other.revalidation?.searchToken;
  const bestRate = combinationAsRate(best);

  return {
    ...primary,
    rateSummary: bestRate ? buildRateSummary(bestRate) : primary.rateSummary,
    rateFeatures: bestRate ? buildRateFeatures(bestRate) : primary.rateFeatures,
    availableMealTypes: availableMealTypes.length > 0 ? availableMealTypes : undefined,
    hasPromotion: primary.hasPromotion || other.hasPromotion || undefined,
    totalPrice: best?.sale ?? primary.totalPrice,
    roomOptions,
    revalidation: buildRevalidation(searchToken, best) ?? primary.revalidation,
  };
}

function hasPromotionValue(value: HotelRate['promotion']): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  return false;
}

function normalizeRemarkText(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (Array.isArray(value)) {
    const joined = value.map(normalizeRemarkText).filter(Boolean).join(' ');
    return joined.length > 0 ? joined : undefined;
  }

  if (value && typeof value === 'object') {
    for (const key of ['text', 'description', 'remark', 'remarks', 'value', 'label']) {
      const remark = normalizeRemarkText((value as Record<string, unknown>)[key]);
      if (remark) return remark;
    }
  }

  return undefined;
}

/**
 * Detects promotion indicators in the provider remarks field.
 * `rate.promotion` is unreliable (often false even when remarks contain promos),
 * so this scans the free-text remarks for known promo patterns.
 */
const PROMO_REMARKS_RE =
  /(?:promo\b[^,;]*)|(?:last\s*minute[^,;]*)|(?:long\s*stay[^,;]*)|(?:oferta[^,;]*)|(?:flex\s*\d+\s*%?)|(?:\d+\s*%\s*off)/i;

function cleanPromotionLabel(label: string): string | undefined {
  const cleaned = label
    .trim()
    .replace(/\s+/g, ' ')
    .split(/[,;:—\-]|\s{2,}/)[0]
    .trim()
    .replace(/\s+([:.])/g, '$1')
    .replace(/(?:\s*[.:])+$/g, '')
    .trim()
    .slice(0, 50)
    .trim();

  return cleaned || undefined;
}

/**
 * Extracts a short, display-ready promotion label from provider remarks.
 * Returns undefined when no known promo pattern is found.
 */
function extractPromotionLabel(remarks: unknown): string | undefined {
  const remarkText = normalizeRemarkText(remarks);
  if (!remarkText) return undefined;
  const match = remarkText.match(PROMO_REMARKS_RE);
  if (!match) return undefined;
  // Normalize provider labels and keep only the first promo when remarks chain several.
  return cleanPromotionLabel(match[0]);
}

function hasPromoRemark(remarks: unknown): boolean {
  const remarkText = normalizeRemarkText(remarks);
  return !!remarkText && PROMO_REMARKS_RE.test(remarkText);
}

function hasPromotionalRate(offer: HotelOffer): boolean {
  return offer.rooms.some((room) =>
    room.rates.some((rate) => hasPromotionValue(rate.promotion) || hasPromoRemark(rate.remarks)),
  );
}

/** Collect all unique, non-empty mealType codes from every rate in every room. */
function collectMealTypes(roomOptions: HotelRoomOption[]): string[] {
  const codes = new Set<string>();
  for (const room of roomOptions) {
    for (const rate of room.rates) {
      if (rate.mealType && rate.mealType.trim()) {
        codes.add(rate.mealType.trim());
      }
    }
  }
  return [...codes];
}

function getHotelInfo(
  info: HotelSearchInfo | undefined,
  hotelId: string,
): HotelInfoEnrichment | undefined {
  const value = info?.[hotelId];
  return typeof value === 'object' && value !== null ? value : undefined;
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function resolveLocalizedText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object') {
    const localized = value as Record<string, unknown>;
    return firstString(localized.es, localized.en, localized.pt, ...Object.values(localized));
  }
  return '';
}

function stripHtml(html: string): string {
  let text = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|h[1-6]|li|tr|td|th|section|article|header|footer)>/gi, ' ');
  text = text.replace(/<[^>]*>/g, '');
  text = text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&aacute;/g, 'á')
    .replace(/&eacute;/g, 'é')
    .replace(/&iacute;/g, 'í')
    .replace(/&oacute;/g, 'ó')
    .replace(/&uacute;/g, 'ú')
    .replace(/&ntilde;/g, 'ñ')
    .replace(/&Aacute;/g, 'Á')
    .replace(/&Eacute;/g, 'É')
    .replace(/&Iacute;/g, 'Í')
    .replace(/&Oacute;/g, 'Ó')
    .replace(/&Uacute;/g, 'Ú')
    .replace(/&Ntilde;/g, 'Ñ')
    .replace(/&uuml;/g, 'ü')
    .replace(/&Uuml;/g, 'Ü')
    .replace(/&ccedil;/g, 'ç')
    .replace(/&Ccedil;/g, 'Ç')
    .replace(/&#(\d+);/g, (_: string, d: string) => String.fromCharCode(Number(d)))
    .replace(/&#x([0-9a-f]+);/gi, (_: string, h: string) => String.fromCharCode(parseInt(h, 16)));
  return text.replace(/\s+/g, ' ').trim();
}

function firstNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return undefined;
}

/**
 * Coarse amenity keywords for fallback extraction from description text.
 * Ordered by coarse priority; only the most useful amenities for card/list display.
 *
 * Each entry: { key: canonical label, patterns: case-insensitive regexes }.
 * The first matching pattern wins for each amenity.
 */
const AMENITY_FALLBACK_RULES: { key: string; patterns: RegExp[] }[] = [
  { key: 'Wi-Fi', patterns: [/wi[\s-]*fi/i, /wifi/i, /internet/i] },
  { key: 'Piscina', patterns: [/piscina/i, /pool/i, /alberca/i] },
  { key: 'Spa', patterns: [/spa/i] },
  { key: 'Gimnasio', patterns: [/gimnasio/i, /fitness/i, /health\s*club/i] },
  { key: 'Restaurante', patterns: [/restaurante/i, /restaurant/i, /diner/i] },
  { key: 'Bar', patterns: [/\bbar\b/i, /bar\s*\/\s*lounge/i, /lobby\s*bar/i] },
  { key: 'Estacionamiento', patterns: [/estacionamiento/i, /parking/i] },
  { key: 'Traslado aeropuerto', patterns: [/traslado\s+aeropuerto/i, /airport\s*shuttle/i] },
  { key: 'Desayuno', patterns: [/desayuno/i, /breakfast/i, /caf[ée]\s*da\s*manh[ãa]/i] },
  { key: 'Centro de negocios', patterns: [/centro\s+de\s+negocios/i, /business\s*center/i] },
  {
    key: 'Salas de reuniones',
    patterns: [/sala(s)?\s+de\s+reuniones/i, /meeting\s*rooms?/i, /conference\s*space/i],
  },
  {
    key: 'Servicio a la habitación',
    patterns: [/servicio\s+a\s+la\s+habitaci[oó]n/i, /room\s*service/i],
  },
  { key: 'Lavandería', patterns: [/lavander[ií]a/i, /laundry/i, /dry\s*cleaning/i] },
  { key: 'Sauna', patterns: [/sauna/i] },
];

/** Extracts amenity labels from a sanitized description text. */
function extractAmenitiesFromDescription(description: string): string[] {
  if (!description) return [];

  const found: string[] = [];
  for (const rule of AMENITY_FALLBACK_RULES) {
    if (rule.patterns.some((re) => re.test(description))) {
      found.push(rule.key);
    }
  }
  return found;
}

function galleryFromInfo(info: HotelInfoEnrichment | undefined, imageUrl: string): string[] {
  const primaryImage = typeof imageUrl === 'string' ? imageUrl.trim() : '';
  const seen = new Set<string>(primaryImage ? [primaryImage] : []);
  const gallery: string[] = [];

  // Prefer the provider gallery when present. Fall back to the primary
  // imageUrl only when there is no gallery source.
  const rawGallery = Array.isArray(info?.galleryImages) ? info.galleryImages : [];
  const rawImages = Array.isArray(info?.images) ? info.images : [];
  const sources: (string | undefined | null)[] = [
    ...rawGallery.map((x) => x),
    ...rawImages.map((x) => x),
  ];

  const MAX_GALLERY = 10;
  for (const src of sources) {
    if (gallery.length >= MAX_GALLERY) break;
    const trimmed = typeof src === 'string' ? src.trim() : '';
    if (!trimmed) continue;
    if (!/^https?:\/\//.test(trimmed)) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    gallery.push(trimmed);
  }

  if (gallery.length) return gallery;

  return primaryImage ? [primaryImage] : [];
}

export function mapOfferToDetailHotel(
  rawOffer: HotelOffer,
  options: DetailMappingOptions = {},
): HotelDetailData {
  // Room and board names arrive with each provider's casing; normalize once for every view.
  const offer = formatOfferLabels(rawOffer);
  const info = getHotelInfo(options.info, offer.hotelId);
  const roomOptions = buildRoomOptionsFromCombinations(buildOfferCombinations(offer), {
    currency: offer.currency,
    supplier: offer.supplier,
  });
  const bestCombination = selectBestCombination(roomOptions);
  // Single-room combinations summarize the provider rate itself; bundles use the combination.
  const bestRate =
    bestCombination?.rateKeys?.length === 1
      ? offer.rooms[0]?.rates.find((rate) => rate.key === bestCombination.rateKeys[0])
      : combinationAsRate(bestCombination);
  const imageUrl = firstString(
    info?.image,
    info?.imageUrl,
    info?.images?.[0],
    info?.galleryImages?.[0],
  );

  const galleryImages = galleryFromInfo(info, imageUrl);
  const hotelName = firstString(info?.name, info?.hotelName, offer.hotelId);

  const hotelTypeId = firstNumber(info?.hotelTypeId, info?.hotelType);

  const availableMealTypes = collectMealTypes(roomOptions);

  const rawDescription = resolveLocalizedText(info?.description);
  const sanitizedDescription = stripHtml(rawDescription);

  // Extract amenities from info enrichment using index signature.
  // When info.amenities is empty/missing, fall back to coarse extraction
  // from the sanitized description text (Spanish-first).
  // ── Coordinates from provider enrichment ──────────────────────────
  const rawLat: unknown = info?.latitude;
  const rawLng: unknown = info?.longitude;
  const latCandidate =
    typeof rawLat === 'number' || typeof rawLat === 'string' ? Number(rawLat) : NaN;
  const lngCandidate =
    typeof rawLng === 'number' || typeof rawLng === 'string' ? Number(rawLng) : NaN;
  const lat = Number.isFinite(latCandidate) ? latCandidate : undefined;

  const lng = Number.isFinite(lngCandidate) ? lngCandidate : undefined;
  const coordinates = lat != null && lng != null ? { lat, lng } : undefined;
  const rawAmenities: unknown = info?.amenities;
  const amenitiesFromInfo: string[] | undefined = Array.isArray(rawAmenities)
    ? (rawAmenities as unknown[]).filter(
        (a): a is string => typeof a === 'string' && a.trim().length > 0,
      )
    : undefined;
  const amenitiesFromDescription = extractAmenitiesFromDescription(sanitizedDescription);
  const amenities = amenitiesFromInfo?.length
    ? amenitiesFromInfo
    : amenitiesFromDescription.length
      ? amenitiesFromDescription
      : undefined;

  return {
    hotelId: offer.hotelId,
    name: hotelName,
    stars: firstNumber(info?.stars),
    rateSummary: buildRateSummary(bestRate),
    rateFeatures: buildRateFeatures(bestRate),
    description: firstString(sanitizedDescription, 'hotels.detail.descriptionUnavailable'),
    imageUrl,
    galleryImages,
    destination: firstString(
      info?.destination,
      options.info?.destination,
      options.fallbackDestination,
    ),
    address: firstString(info?.address),
    hotelTypeId,
    availableMealTypes: availableMealTypes.length > 0 ? availableMealTypes : undefined,
    hasPromotion: hasPromotionalRate(offer) || undefined,
    amenities: amenities?.length ? amenities : undefined,
    coordinates,
    supplier: offer.supplier,
    currency: offer.currency,
    totalPrice: bestCombination?.sale ?? 0,
    roomOptions,
    revalidation: buildRevalidation(options.searchToken, bestCombination),
  };
}
